import React, { useEffect, useState } from "react";
import api, { extractError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DictationTextarea } from "@/components/DictationTextarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { TaskStatusBadge, PriorityBadge } from "@/components/Badges";
import { EmptyState } from "@/components/Common";
import { PRIORITIES, TASK_STATUSES } from "@/lib/constants";
import { personName, fmtDateTime, isOverdue } from "@/lib/format";
import { Loader2, Check, AlertTriangle, CalendarPlus, Briefcase } from "lucide-react";
import { toast } from "sonner";

// Data/ora odierna (locale) in formato per input datetime-local
const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export const FOLLOWUP_TYPES = ["Appuntamento", "Corso", "Attività"];

// Pop-up "Gestione servizi": registra il servizio erogato + categorie correlate (dal menù Servizi)
// e una nota. Modalità ATTIVITÀ (task) -> collega/aggiorna il servizio della pratica e porta
// l'attività "In corso". Modalità PRATICA (caseId) -> aggiorna il servizio della pratica.
export const GestioneServiziDialog = ({ open, setOpen, task, caseId, defaultServizioId, numero, onDone }) => {
  const [services, setServices] = useState([]);
  const [servizioId, setServizioId] = useState("");
  const [categorie, setCategorie] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const isTask = !!task?.id;
  const numeroPratica = task?.numero_pratica ?? numero;
  useEffect(() => {
    if (!open) return;
    api.get("/services").then(({ data }) => setServices(data.filter((s) => s.attivo))).catch((e) => console.error("Errore servizi", e));
    setServizioId(task?.servizio_id || defaultServizioId || "");
    setCategorie([]);
    setNote("");
  }, [open, task, defaultServizioId]);
  const svc = services.find((s) => s.id === servizioId);
  const cats = svc?.categorie || [];
  const toggleCat = (c) => setCategorie((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));
  const save = async () => {
    if (!servizioId) { toast.error("Seleziona un servizio"); return; }
    if (!isTask && !caseId) { toast.error("Contesto non valido"); return; }
    setSaving(true);
    try {
      const payload = { servizio_id: servizioId, categorie, note: note || null };
      if (isTask) {
        await api.post(`/tasks/${task.id}/gestione-servizio`, payload);
        toast.success("Servizio registrato · attività In corso");
      } else {
        await api.post(`/cases/${caseId}/gestione-servizio`, payload);
        toast.success("Servizio della pratica aggiornato");
      }
      setOpen(false);
      onDone && onDone();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-auto ras-scroll" data-testid="gestione-servizi-dialog">
        <DialogHeader><DialogTitle className="font-display">Gestione servizi</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Registra il servizio erogato{numeroPratica ? ` per la pratica ${numeroPratica}` : ""} e le eventuali categorie correlate. {isTask ? 'L\u2019attività passerà in stato "In corso".' : "Il servizio della pratica verrà aggiornato."}</p>
        <div className="space-y-3 mt-1">
          <div><Label>Servizio *</Label>
            <Select value={servizioId} onValueChange={setServizioId}>
              <SelectTrigger data-testid="gestione-servizio-select"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
              <SelectContent className="max-h-64">{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {svc && (
            <div>
              <Label>Categorie correlate</Label>
              {cats.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1">Nessuna categoria definita per questo servizio.</p>
              ) : (
                <div className="mt-1 grid sm:grid-cols-2 gap-2 max-h-52 overflow-auto ras-scroll">
                  {cats.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border px-2.5 py-2 hover:bg-muted" data-testid="gestione-categoria">
                      <Checkbox checked={categorie.includes(c)} onCheckedChange={() => toggleCat(c)} />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
          <div><Label>Note</Label><DictationTextarea value={note} onValueChange={setNote} rows={3} placeholder="Scrivi o detta con il microfono…" data-testid="gestione-note" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-green-700)] hover:bg-[color:var(--brand-green-600)] text-white" data-testid="gestione-save-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva e avvia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Elenco attività COMPLETABILI: consente di cambiare stato, completare l'iter, pianificare il
// prossimo step e aprire la "Gestione servizi" per ciascuna attività, con aggiornamento (onChanged).
export const TaskCompletableList = ({ tasks = [], canWrite = false, onChanged, showPerson = false }) => {
  const [busyId, setBusyId] = useState(null);
  const [stepTask, setStepTask] = useState(null);
  const [svcTask, setSvcTask] = useState(null);
  const changeStatus = async (t, stato) => {
    if (stato === t.stato) return;
    setBusyId(t.id);
    try {
      await api.put(`/tasks/${t.id}`, { stato });
      toast.success(stato === "Completata" ? "Attività completata" : "Stato aggiornato");
      onChanged && onChanged();
    } catch (e) { toast.error(extractError(e)); } finally { setBusyId(null); }
  };
  if (!tasks.length) return <EmptyState title="Nessuna attività" />;
  return (
    <>
      <Card className="divide-y">
        {tasks.map((t) => (
          <div key={t.id} className="p-3 flex flex-col gap-2" data-testid="task-row">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-2">
                  {t.titolo}
                  {isOverdue(t) && <span className="inline-flex items-center gap-1 text-[11px] text-[color:var(--brand-red-600)]"><AlertTriangle className="h-3 w-3" /> Scaduta</span>}
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5">
                  {showPerson && t.persona_nome && <span className="font-semibold text-[color:var(--brand-blue-700)]" data-testid="task-persona-nome">{t.persona_nome}</span>}
                  {t.numero_pratica && <span className="font-semibold text-[color:var(--brand-orange-600)]" data-testid="task-numero-pratica">{t.numero_pratica}</span>}
                  {t.assegnatario_nome && <span className="font-semibold text-[color:var(--brand-green-700)]" data-testid="task-gestore">Gestore: {t.assegnatario_nome}</span>}
                  <span>Scadenza: {fmtDateTime(t.data_scadenza)}{t.categoria ? ` · ${t.categoria}` : ""}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <PriorityBadge priorita={t.priorita} />
                {canWrite ? (
                  <>
                    <Select value={t.stato} onValueChange={(v) => changeStatus(t, v)}>
                      <SelectTrigger className="w-[140px] h-9" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    {t.stato !== "Completata" && (
                      <Button size="sm" variant="outline" onClick={() => changeStatus(t, "Completata")}
                        disabled={busyId === t.id} data-testid="task-complete-button" title="Completa attività">
                        {busyId === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </Button>
                    )}
                  </>
                ) : <TaskStatusBadge stato={t.stato} />}
              </div>
            </div>
            {canWrite && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setStepTask(t)}
                  className="border-[color:var(--brand-blue-600)] text-[color:var(--brand-blue-700)] hover:bg-[color:var(--brand-blue-100)]"
                  data-testid="task-pianifica-step-button">
                  <CalendarPlus className="h-4 w-4 mr-1" /> Pianifica prossimo step
                </Button>
                <Button size="sm" onClick={() => setSvcTask(t)}
                  className="bg-[color:var(--brand-green-700)] hover:bg-[color:var(--brand-green-600)] text-white"
                  data-testid="task-gestione-servizi-button">
                  <Briefcase className="h-4 w-4 mr-1" /> Gestione servizi
                </Button>
              </div>
            )}
          </div>
        ))}
      </Card>

      <PianificaStepDialog
        open={!!stepTask}
        setOpen={(v) => { if (!v) setStepTask(null); }}
        ctx={stepTask ? { caseId: stepTask.case_id, personId: stepTask.person_id, servizioId: stepTask.servizio_id, numero: stepTask.numero_pratica } : null}
        onDone={() => { setStepTask(null); onChanged && onChanged(); }}
      />
      <GestioneServiziDialog
        open={!!svcTask}
        setOpen={(v) => { if (!v) setSvcTask(null); }}
        task={svcTask}
        onDone={() => { setSvcTask(null); onChanged && onChanged(); }}
      />
    </>
  );
};

// Dialog "Pianifica prossimo step": iscrive a calendario (Agenda) un Appuntamento/Corso/Attività.
// Accetta `caseData` (dalla pratica) OPPURE `ctx` = {caseId, personId, servizioId, numero} (da un'attività).
export const PianificaStepDialog = ({ open, setOpen, caseData, ctx, defaultTipo = "Appuntamento", onDone }) => {
  const c = caseData?.pratica;
  const p = caseData?.persona;
  const caseId = ctx?.caseId ?? c?.id ?? null;
  const personId = ctx?.personId ?? p?.id ?? null;
  const servizioId = ctx?.servizioId ?? c?.servizio_id ?? null;
  const numero = ctx?.numero ?? c?.numero ?? null;
  const [f, setF] = useState({ tipo: defaultTipo, titolo: "", data_inizio: nowLocal(), luogo: "", note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF({ tipo: defaultTipo || "Appuntamento", titolo: "", data_inizio: nowLocal(), luogo: "", note: "" });
  }, [open, defaultTipo]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    if (!f.data_inizio) { toast.error("Indica data e ora"); return; }
    setSaving(true);
    try {
      await api.post("/appointments", {
        titolo: f.titolo, tipo: f.tipo,
        data_inizio: new Date(f.data_inizio).toISOString(),
        person_id: personId, case_id: caseId,
        servizio_id: servizioId, luogo: f.luogo || null, note: f.note || null,
      });
      toast.success(`${f.tipo} inserito in Agenda`);
      setOpen(false);
      onDone && onDone();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Pianifica il prossimo step</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Registra il primo appuntamento, il corso o l'attività a calendario{numero ? ` per la pratica ${numero}` : ""}.</p>
        <div className="space-y-3 mt-1">
          <div><Label>Tipo di attività</Label>
            <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="step-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{FOLLOWUP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label htmlFor="step-title">Titolo *</Label>
            <Input id="step-title" value={f.titolo} onChange={(e) => set("titolo", e.target.value)}
              placeholder="Es. Primo colloquio, Corso genitorialità…" data-testid="step-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="step-date">Data e ora *</Label>
              <Input id="step-date" type="datetime-local" value={f.data_inizio} onChange={(e) => set("data_inizio", e.target.value)} data-testid="step-date-input" /></div>
            <div><Label htmlFor="step-luogo">Luogo</Label>
              <Input id="step-luogo" value={f.luogo} onChange={(e) => set("luogo", e.target.value)} data-testid="step-luogo-input" /></div>
          </div>
          <div><Label htmlFor="step-note">Note</Label>
            <DictationTextarea id="step-note" value={f.note} onValueChange={(v) => set("note", v)} rows={2} placeholder="Scrivi o detta con il microfono…" data-testid="step-note-input" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="step-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Aggiungi all'agenda</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CaseDialog = ({ open, setOpen, personId, onDone }) => {
  const [services, setServices] = useState([]);
  const [team, setTeam] = useState([]);
  const [f, setF] = useState({ servizio_id: "", priorita: "Normale", descrizione: "", canale: "Telefono", responsabile_id: "" });
  const [saving, setSaving] = useState(false);
  const [pQuery, setPQuery] = useState("");
  const [pResults, setPResults] = useState([]);
  const [pSel, setPSel] = useState(null);
  useEffect(() => {
    if (!open) return;
    api.get("/services").then(({ data }) => setServices(data.filter((s) => s.attivo))).catch(() => {});
    api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
    setPSel(null); setPQuery("");
  }, [open]);
  useEffect(() => {
    if (personId || !pQuery || pQuery.length < 2) { setPResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/persons?q=${encodeURIComponent(pQuery)}&limit=6`); setPResults(data.items); } catch (e) { console.error("Errore ricerca persona", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [pQuery, personId]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    const pid = personId || pSel?.id;
    if (!pid) { toast.error("Seleziona una persona"); return; }
    if (!f.servizio_id) { toast.error("Seleziona un servizio"); return; }
    if (!f.descrizione.trim()) { toast.error("Inserisci la descrizione della richiesta"); return; }
    setSaving(true);
    try {
      const payload = { person_id: pid, servizio_id: f.servizio_id, priorita: f.priorita,
        descrizione: f.descrizione, canale: f.canale, responsabile_id: f.responsabile_id || null };
      const { data } = await api.post("/cases", payload);
      toast.success(`Pratica ${data.numero} creata`);
      setOpen(false); setF({ servizio_id: "", priorita: "Normale", descrizione: "", canale: "Telefono", responsabile_id: "" });
      onDone && onDone(data);
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-auto ras-scroll">
        <DialogHeader><DialogTitle className="font-display">Apri nuova pratica</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!personId && (
            <div><Label>Persona *</Label>
              {pSel ? (
                <div className="flex items-center justify-between rounded-lg border bg-[color:var(--brand-green-100)] p-2.5">
                  <span className="text-sm font-medium">{personName(pSel)} · {pSel.telefono_principale || "-"}</span>
                  <Button variant="ghost" size="sm" onClick={() => setPSel(null)}>Cambia</Button>
                </div>
              ) : (
                <>
                  <Input value={pQuery} onChange={(e) => setPQuery(e.target.value)} placeholder="Cerca per nome o telefono" data-testid="case-person-search" />
                  <div className="space-y-1 mt-1 max-h-40 overflow-auto ras-scroll">
                    {pResults.map((p) => (
                      <button key={p.id} onClick={() => { setPSel(p); setPResults([]); }} className="w-full text-left rounded border p-2 text-sm hover:bg-muted">
                        {personName(p)} <span className="text-xs text-muted-foreground">{p.telefono_principale}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <div><Label>Servizio *</Label>
            <Select value={f.servizio_id} onValueChange={(v) => set("servizio_id", v)}>
              <SelectTrigger data-testid="case-service-select"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
              <SelectContent className="max-h-64">{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Priorità</Label>
              <Select value={f.priorita} onValueChange={(v) => set("priorita", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Canale</Label>
              <Select value={f.canale} onValueChange={(v) => set("canale", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Telefono", "Email", "WhatsApp", "Di persona", "Portale pubblico"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Responsabile (opzionale)</Label>
            <Select value={f.responsabile_id} onValueChange={(v) => set("responsabile_id", v)}>
              <SelectTrigger><SelectValue placeholder="Assegna automaticamente dal servizio" /></SelectTrigger>
              <SelectContent className="max-h-64">{team.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome_completo} · {u.ruolo_label}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Descrizione della richiesta *</Label><Textarea value={f.descrizione} onChange={(e) => set("descrizione", e.target.value)} rows={3} data-testid="case-description-input" /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="case-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Crea pratica</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const TaskDialog = ({ open, setOpen, personId = null, caseId = null, onDone }) => {
  const [team, setTeam] = useState([]);
  const [f, setF] = useState({ titolo: "", descrizione: "", priorita: "Normale", data_scadenza: "", assegnatario_id: "", categoria: "Follow-up" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) api.get("/team").then(({ data }) => setTeam(data)).catch(() => {}); }, [open]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.titolo.trim()) { toast.error("Inserisci un titolo"); return; }
    setSaving(true);
    try {
      const payload = { titolo: f.titolo, descrizione: f.descrizione || null, priorita: f.priorita,
        categoria: f.categoria, person_id: personId, case_id: caseId,
        assegnatario_id: f.assegnatario_id || null,
        data_scadenza: f.data_scadenza ? new Date(f.data_scadenza).toISOString() : null };
      const { data } = await api.post("/tasks", payload);
      toast.success("Attività creata");
      setOpen(false); setF({ titolo: "", descrizione: "", priorita: "Normale", data_scadenza: "", assegnatario_id: "", categoria: "Follow-up" });
      onDone && onDone(data);
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Crea attività</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titolo *</Label><Input value={f.titolo} onChange={(e) => set("titolo", e.target.value)} data-testid="task-title-input" /></div>
          <div><Label>Descrizione</Label><Textarea value={f.descrizione} onChange={(e) => set("descrizione", e.target.value)} rows={2} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Priorità</Label>
              <Select value={f.priorita} onValueChange={(v) => set("priorita", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Scadenza</Label><Input type="datetime-local" value={f.data_scadenza} onChange={(e) => set("data_scadenza", e.target.value)} /></div>
          </div>
          <div><Label>Assegnatario</Label>
            <Select value={f.assegnatario_id} onValueChange={(v) => set("assegnatario_id", v)}>
              <SelectTrigger><SelectValue placeholder="Assegna a me" /></SelectTrigger>
              <SelectContent className="max-h-64">{team.map((u) => <SelectItem key={u.id} value={u.id}>{u.nome_completo} · {u.ruolo_label}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="task-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva attività</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
